import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import mapboxgl from "!mapbox-gl"; // eslint-disable-line import/no-webpack-loader-syntax

import Popup from "../../popup";
import useSources from "../useSources";
import useLayers from "../useLayers";
import {
  buildFilterExpression,
  getUniqueFeatures,
  MapLogger,
} from "./mapUtils";
import { BASEMAP_STYLES, WELLS_LAYER_ID } from "../../constants";
import createTheme from "../../../../theme";
import { ThemeProvider } from "styled-components/macro";
import {
  jssPreset,
  StylesProvider,
  ThemeProvider as MuiThemeProvider,
} from "@material-ui/core/styles";
import { create } from "jss";
import { useMapResize } from "../useMapResize";
import { useMapControls } from "../useMapControls";

const mapLogger = new MapLogger({
  enabled: process.env.NODE_ENV === "development",
  prefix: "Public Map",
});

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

const jss = create({
  ...jssPreset(),
  insertionPoint: document.getElementById("jss-insertion-point"),
});

// layers that the location filters are applied to
const FILTERED_LAYER_IDS = [WELLS_LAYER_ID, "locations-symbol"];

/**
 * The `useMap` hook controls all of the Mapbox functionality. It controls
 * everything from rendering the map and popups to filtering layers to styling
 * layers.
 * The hook exposes the map instance in addition to a variety of handlers
 * responsible for applying filters and styles to the map
 * `isMapReady` is true once the sources, layers and events are added.
 * `styleVersion` increments every time the layers are (re)added to the map, so
 * consumers can re-apply filters and paint after a basemap change
 * @param {object} ref a React ref for the map container
 * @param {*} mapConfig initial configuration options for the map
 * @param {Array} swapVisibility optional array of layer ids to flip visibility on
 * @param {function} applyLayerOverrides optional callback to override the
 * initial layer configuration (see useLayers)
 * see https://docs.mapbox.com/mapbox-gl-js/api/map/
 */
const useMap = ({
  ref,
  mapConfig,
  swapVisibility = [],
  applyLayerOverrides,
  // when true the location layers are added hidden until the consumer calls
  // revealLocationLayers (once filters and colors have been applied)
  deferLocationReveal = false,
  initialBasemap = BASEMAP_STYLES[0].style,
  setResultsPanelVisible = () => {},
  mode = "map-explorer",
  flowLocationIds,
}) => {
  // Map related state
  const [map, setMap] = useState(null);
  const [activeBasemap, setActiveBasemap] = useState(initialBasemap);
  const [eventsRegistered, setEventsRegistered] = useState(false);
  const [styleVersion, setStyleVersion] = useState(0);

  // Data related state
  const [currentPapFeatures, setCurrentPapFeatures] = useState([]);
  // the location layers are added hidden and revealed once the filters and
  // colors have been applied, so unfiltered default colored points never flash
  const [areLocationsRevealed, setAreLocationsRevealed] = useState(false);
  const [dataAdded, setDataAdded] = useState(false);
  const [selectedMonitoringLocation, setSelectedMonitoringLocation] =
    useState(null);
  const [dataVizGraphType, setDataVizGraphType] = useState(null);

  // the use of useRef allows the mapbox click handler to have access
  // to the current value of mode, instead of its value when the click handler
  // was defined
  const modeRef = useRef(mode);
  useEffect(() => {
    modeRef.current = mode;
  }, [mode]);

  const flowLocationIdsRef = useRef(flowLocationIds);
  useEffect(() => {
    flowLocationIdsRef.current = flowLocationIds;
  }, [flowLocationIds]);

  const setResultsPanelVisibleRef = useRef(setResultsPanelVisible);
  useEffect(() => {
    setResultsPanelVisibleRef.current = setResultsPanelVisible;
  }, [setResultsPanelVisible]);

  // Define all refs
  const popUpRef = useRef(
    new mapboxgl.Popup({
      maxWidth: "400px",
      offset: 15,
      focusAfterOpen: false,
    })
  );

  // resize the map when the map container ref size changes
  useMapResize({ map, ref });

  // Add basic map controls
  useMapControls({ map });

  // Fetch a list of sources  and layers to add to the map
  const { sources } = useSources();
  const { layers, setLayers, defaultLayers } = useLayers(
    swapVisibility,
    applyLayerOverrides
  );

  /**
   * Initialize the map once and tear it down on unmount
   * Once the map is loaded, store a reference to the map in
   * our application state
   * Removing the map on unmount releases its WebGL context, browsers cap the
   * number of active contexts
   */
  useEffect(() => {
    if (!ref?.current) return;
    let isMounted = true;
    const popup = popUpRef.current;

    const mapInstance = new mapboxgl.Map({
      container: ref.current,
      ...mapConfig,
    });

    if (process.env.NODE_ENV === "development") {
      // exposes the map instance for debugging and browser tests
      window.__publicMap = mapInstance;
    }

    mapInstance.on("load", () => {
      if (!isMounted) return;
      mapLogger.log("Map loaded");
      setMap(mapInstance);
    });

    return () => {
      isMounted = false;
      popup.remove();
      mapInstance.remove();
      if (window.__publicMap === mapInstance) delete window.__publicMap;
    };
    // the map is only created on mount, later config changes are applied
    // through the map instance
  }, []); //eslint-disable-line

  /**
   * Function responsible for adding sources and layers to the map
   * There are a number of checks in place to ensure that sources
   * only get added to the map once the map and sources are loaded and
   * to ensure that layers are only added to the map once the layers are
   * loaded and the associated sources are added to the map
   */
  useEffect(() => {
    const shouldAddData =
      !!map && sources?.length > 0 && layers?.length > 0 && !dataAdded;
    if (!shouldAddData) return;

    sources.forEach((source) => {
      const { id, ...rest } = source;
      if (!map.getSource(id)) {
        map.addSource(id, rest);
      }
    });

    mapLogger.log("Sources added to map");

    [...layers]
      .sort((a, b) => ((a.drawOrder || 0) > (b.drawOrder || 0) ? -1 : 1))
      .forEach((layer) => {
        const { lreProperties, ...rest } = layer;
        if (map.getLayer(layer.id)) return;
        const shouldHide =
          deferLocationReveal && FILTERED_LAYER_IDS.includes(layer.id);
        map.addLayer(
          shouldHide
            ? { ...rest, layout: { ...rest.layout, visibility: "none" } }
            : rest
        );
      });

    mapLogger.log("Layers added to map");

    setDataAdded(true);
    setAreLocationsRevealed(!deferLocationReveal);
    setStyleVersion((prevState) => prevState + 1);
  }, [dataAdded, layers, map, sources]); //eslint-disable-line

  useEffect(() => {
    const shouldAddClickEvent = !!map && layers?.length > 0 && dataAdded;
    if (!shouldAddClickEvent || eventsRegistered) return;

    //MJB add event listener for all circle and symbol layers
    // pointer on mouseover
    const cursorPointerLayerIds = layers
      .filter(
        (layer) =>
          (["circle", "symbol"].includes(layer.type) ||
            layer.id.includes("pap")) &&
          !layer.lreProperties?.popup?.excludePopup
      )
      .map((layer) => layer.id);
    cursorPointerLayerIds.forEach((layerId) => {
      map.on("mouseenter", layerId, () => {
        map.getCanvas().style.cursor = "pointer";
      });
      map.on("mouseleave", layerId, () => {
        map.getCanvas().style.cursor = "";
      });
    });

    const papLayers = layers
      .filter((layer) => layer.id.includes("pap"))
      .map((layer) => layer.id);

    map.on("moveend", () => {
      const papFeatures = map.queryRenderedFeatures({
        layers: papLayers.filter((layerId) => map.getLayer(layerId)),
      });
      if (papFeatures) {
        const uniqueFeatures = getUniqueFeatures(papFeatures, "ID");
        setCurrentPapFeatures(uniqueFeatures);
      }
    });

    map.on("click", (e) => {
      const features = map.queryRenderedFeatures(e.point);

      //Ensure that if the map is zoomed out such that multiple
      //copies of the feature are visible, the popup appears
      //over the copy being pointed to.
      //only for features with the properties.lat/long field (clearwater wells)
      const coordinates = features[0]?.properties?.latitude_dd
        ? [
            features[0].properties.longitude_dd,
            features[0].properties.latitude_dd,
          ]
        : [e.lngLat.lng, e.lngLat.lat];

      //MJB add check for popups so they only appear on our dynamic layers
      const popupLayerIds = layers
        .filter((layer) => !layer?.lreProperties?.popup?.excludePopup)
        .map((layer) => layer.id);
      if (features.length > 0 && popupLayerIds.includes(features[0].layer.id)) {
        if (
          features[0]?.layer?.id === WELLS_LAYER_ID &&
          modeRef.current === "graph-mode"
        ) {
          setSelectedMonitoringLocation(features[0]?.properties);
          setResultsPanelVisibleRef.current(true);
        }

        const myFeatures = features.filter((feature) =>
          popupLayerIds.includes(feature?.layer?.id)
        );
        // create popup node
        const popupNode = document.createElement("div");
        ReactDOM.render(
          //MJB adding style providers to the popup
          <StylesProvider jss={jss}>
            <MuiThemeProvider theme={createTheme()}>
              <ThemeProvider theme={createTheme()}>
                <Popup
                  layers={layers}
                  features={myFeatures}
                  flowLocationIds={flowLocationIdsRef.current}
                />
              </ThemeProvider>
            </MuiThemeProvider>
          </StylesProvider>,
          popupNode
        );
        popUpRef.current
          .setLngLat(coordinates)
          .setDOMContent(popupNode)
          .addTo(map);
        // unmount the popup's React tree when it closes or is replaced
        popUpRef.current.once("close", () =>
          ReactDOM.unmountComponentAtNode(popupNode)
        );
      }
    });

    setEventsRegistered(true);
    mapLogger.log("Event handlers attached to map");
  }, [map, layers, dataAdded, eventsRegistered]); //eslint-disable-line

  /**
   * Applies the filter values to the location layers
   * Consumers call this from an effect keyed on the filter values, `isMapReady`
   * and `styleVersion`
   * @param {object} filterValues object representing all of the current
   * filter values
   */
  const applyLayerFilters = useCallback(
    (filterValues) => {
      if (!map) return;
      const mapFilterExpression = buildFilterExpression(filterValues);
      FILTERED_LAYER_IDS.forEach((layerId) => {
        if (map.getLayer(layerId)) {
          map.setFilter(layerId, mapFilterExpression);
        }
      });
      mapLogger.log("Filters updated on the locations layers");
    },
    [map]
  );

  /**
   * Handler used to update paint styles applied to map layer
   * This is used to support the color wells by x control
   * We look through each provided paint style property and apply the
   * style rule to the layer. The paint is also persisted on the layer state
   * so the layer legend reflects it
   * Reference https://docs.mapbox.com/mapbox-gl-js/api/map/#map#setpaintproperty
   * @param {object} style object with a `layerId` and `paint` rules
   */
  const applyLayerPaint = useCallback(
    (style) => {
      if (!map || !map.getLayer(style.layerId)) return;
      Object.entries(style.paint).forEach(([ruleName, ruleValue]) => {
        map.setPaintProperty(style.layerId, ruleName, ruleValue);
      });
      setLayers((prevState) =>
        prevState.map((d) => {
          if (d.id !== style.layerId) return d;
          return {
            ...d,
            paint: {
              ...d.paint,
              ...style.paint,
            },
          };
        })
      );
      mapLogger.log(`Paint styles updated on the ${style.layerId} layer`);
    },
    [map, setLayers]
  );

  /**
   * Handler used to update the visibility property on a layer
   * We employ special logic in this handler to allow for toggling
   * the visibility of grouped layers on and off
   * This allows us to display a single item in the layers list
   * but to control the visibility of multiple map layers at once
   * A common use case would be for something like parcels where
   * you want to display a layer for the parcel outlines and a layer
   * for the parcel fill.
   * This approach allows us to only show one layer in
   * the layer control list but to turn both layers on/off
   * @param {string | number} options.id ID associated with the layer or layer group
   * @param {boolean} options.boolean whether the layer is on/off
   */
  const updateLayerVisibility = ({ id, visible }) => {
    if (!map) return;
    const visibleValue = visible ? "visible" : "none";
    const isInGroup = (layer) =>
      (layer?.lreProperties?.layerGroup || layer.id) === id;

    layers.filter(isInGroup).forEach((layer) => {
      if (map.getLayer(layer.id)) {
        map.setLayoutProperty(layer.id, "visibility", visibleValue);
      }
    });

    setLayers((prevState) =>
      prevState.map((layer) => {
        if (!isInGroup(layer)) return layer;
        return {
          ...layer,
          layout: {
            ...layer?.layout,
            visibility: visibleValue,
          },
        };
      })
    );
    mapLogger.log(`Visibility set to ${visibleValue} for the ${id} layer`);
  };

  const updateLayerOpacity = useCallback(
    ({ id, opacity }) => {
      if (!map) return;
      if (map.getLayer(id)) {
        map.setPaintProperty(id, "fill-opacity", opacity);
      }
      setLayers((prevState) =>
        prevState.map((layer) => {
          if (layer.id !== id) return layer;
          return {
            ...layer,
            paint: {
              ...layer.paint,
              "fill-opacity": opacity,
            },
          };
        })
      );
      mapLogger.log(`Opacity set to ${opacity} for the ${id} layer`);
    },
    [map, setLayers]
  );

  /**
   * Shows the location layers, honouring the visibility in state (the layers
   * control and the URL can turn them off)
   */
  const revealLocationLayers = useCallback(() => {
    if (!map) return;
    layers
      .filter((layer) => FILTERED_LAYER_IDS.includes(layer.id))
      .forEach((layer) => {
        if (!map.getLayer(layer.id)) return;
        map.setLayoutProperty(
          layer.id,
          "visibility",
          layer.layout?.visibility === "visible" ? "visible" : "none"
        );
      });
    setAreLocationsRevealed(true);
  }, [map, layers]);

  /**
   * Restores the backend default visibility and fill opacity for every layer
   * Paint set by the "Color Data Points by" control is kept
   */
  const resetLayers = useCallback(() => {
    if (!map) return;
    const defaultsById = new Map(
      defaultLayers.map((layer) => [layer.id, layer])
    );
    const getDefaults = (layer) => {
      const defaultLayer = defaultsById.get(layer.id);
      if (!defaultLayer) return null;
      const opacity = defaultLayer.paint?.["fill-opacity"];
      return {
        visibility:
          defaultLayer.layout?.visibility === "visible" ? "visible" : "none",
        opacity:
          layer.type === "fill" && typeof opacity === "number"
            ? opacity
            : undefined,
      };
    };

    layers.forEach((layer) => {
      const defaults = getDefaults(layer);
      if (!defaults || !map.getLayer(layer.id)) return;
      map.setLayoutProperty(layer.id, "visibility", defaults.visibility);
      if (defaults.opacity !== undefined) {
        map.setPaintProperty(layer.id, "fill-opacity", defaults.opacity);
      }
    });

    setLayers((prevState) =>
      prevState.map((layer) => {
        const defaults = getDefaults(layer);
        if (!defaults) return layer;
        return {
          ...layer,
          layout: { ...layer.layout, visibility: defaults.visibility },
          paint:
            defaults.opacity === undefined
              ? layer.paint
              : { ...layer.paint, "fill-opacity": defaults.opacity },
        };
      })
    );
  }, [map, layers, defaultLayers, setLayers]);

  /**
   * Changing the style removes every source and layer from the map
   * Once the new style has loaded, flag the data as not added so the sources
   * and layers are re-added from state (which bumps `styleVersion`)
   */
  const updateBasemap = (style) => {
    if (!map) return;
    map.once("style.load", () => {
      setActiveBasemap(style.style);
      setDataAdded(false);
    });
    // diff: false forces a full style load so "style.load" always fires
    map.setStyle(style.url, { diff: false });
  };

  return {
    activeBasemap,
    basemaps: BASEMAP_STYLES,
    layers,
    defaultLayers,
    map,
    sources,
    isMapReady: !!map && dataAdded && eventsRegistered,
    styleVersion,
    updateBasemap,
    applyLayerFilters,
    applyLayerPaint,
    updateLayerVisibility,
    revealLocationLayers,
    areLocationsRevealed,
    resetLayers,
    selectedMonitoringLocation,
    setSelectedMonitoringLocation,
    dataVizGraphType,
    setDataVizGraphType,
    eventsRegistered,
    currentPapFeatures,
    updateLayerOpacity,
  };
};

export { useMap };
